import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { QueryList, Injectable, inject, PLATFORM_ID, ElementRef, Directive, Input, ChangeDetectorRef, HostBinding, HostListener, NgModule } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { skip } from 'rxjs/operators';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { correctKeyRTL } from '@simpl/element-ng/common';

class SiListInteractionService {
    interactableLists = [];
    interactableListItems = [];
    changeQueue = [];
    queueTimer;
    /**
     * Add an interactable list and add it to `listsAndItemsChanges`.
     * Return the interactable list object.
     */
    addInteractableList(directive, element, triggerDefined, position, orientation, focusLastFromParent, isSubList, calculatedCallback) {
        const list = {
            directive,
            element,
            items: [],
            integratedNestedItems: [],
            integratedParentItem: null,
            allItemsQueryList: new QueryList(),
            nested: [],
            nestedDisconnected: [],
            parent: null,
            parentDisconnected: null,
            trigger: null,
            triggerDefined: new BehaviorSubject(triggerDefined),
            position: new BehaviorSubject(position),
            orientation: new BehaviorSubject(orientation),
            focusLastFromParent: new BehaviorSubject(focusLastFromParent),
            changeToProcess: true,
            isSubList,
            calculatedCallback
        };
        this.enqueueChange({ object: list, type: 'list', change: 'add' });
        return list;
    }
    /**
     * Remove an interactable list and add it to `listsAndItemsChanges`.
     */
    removeInteractableList(list) {
        this.enqueueChange({ object: list, type: 'list', change: 'remove' });
    }
    /**
     * Add an interactable list item and add it to `listsAndItemsChanges`.
     * Return the interactable list item object.
     */
    addInteractableListItem(directive, element) {
        const item = {
            directive,
            element,
            parent: null,
            child: null,
            parentIntegrationIndex: -1,
            nestedIntegration: false
        };
        this.enqueueChange({ object: item, type: 'item', change: 'add' });
        return item;
    }
    /**
     * Remove an interactable list item and add it to `listsAndItemsChanges`.
     */
    removeInteractableListItem(item) {
        this.enqueueChange({ object: item, type: 'item', change: 'remove' });
    }
    /**
     * Check if a HTMLElement is a child of a interactable list item.
     * First properly follows any possible dropdown containers through the DOM chain,
     * then continues of the list interaction chain.
     */
    isChildElementOfItem(item, element) {
        let currentItem = this.getClosestRelativeItem(element, true);
        if (currentItem) {
            do {
                if (currentItem.element === item.element) {
                    return true;
                }
                currentItem = currentItem.parent
                    ? currentItem.parent.trigger ??
                        (currentItem.parent.parentDisconnected
                            ? currentItem.parent.parentDisconnected.trigger
                            : null)
                    : null;
            } while (currentItem);
        }
        return false;
    }
    enqueueChange(change) {
        this.changeQueue.push(change);
        if (this.queueTimer) {
            return;
        }
        this.queueTimer = setTimeout(() => {
            this.processChanges(this.changeQueue);
            this.changeQueue = [];
            this.queueTimer = undefined;
        });
    }
    /**
     * Process the changes after the buffer and queue up all the necessary
     * operations and callbacks. Then call them.
     */
    processChanges(changes) {
        const operations = [
            'listSetChildItems',
            'listSetNestedLists',
            'listSetTriggerItem',
            'listSetIntegratedItems'
        ];
        const allOperations = {
            'listSetChildItems': [],
            'listSetNestedLists': [],
            'listSetTriggerItem': [],
            'listSetIntegratedItems': []
        };
        const operationsToProcess = {
            'listSetChildItems': { lists: [], callbacks: [] },
            'listSetNestedLists': { lists: [], callbacks: [] },
            'listSetTriggerItem': { lists: [], callbacks: [] },
            'listSetIntegratedItems': { lists: [], callbacks: [] }
        };
        /**
         * Add a callback function to an operation in `operationsToProcess`.
         */
        const addOperationCallback = (operation, callback) => {
            operationsToProcess[operation].callbacks.push(callback);
        };
        /**
         * Add a list to an operation in `operationsToProcess`, only if there is not
         * already one inside.
         */
        const addOperation = (operation, list, callback) => {
            const operationObject = operationsToProcess[operation];
            const allArray = allOperations[operation];
            if (!allArray.find(operationList => operationList.element === list.element)) {
                operationObject.lists.push(list);
                allArray.push(list);
            }
            if (operation === 'listSetChildItems' || operation === 'listSetNestedLists') {
                removeOperation('listSetIntegratedItems', list);
            }
            if (callback) {
                addOperationCallback(operation, callback);
            }
        };
        /**
         * Remove a list from an operation in `allOperations`.
         */
        const removeOperation = (operation, list) => {
            const operationObject = allOperations[operation];
            const listIndex = operationObject.findIndex(searchList => searchList.element === list.element);
            if (listIndex !== -1) {
                operationObject.splice(listIndex, 1);
            }
        };
        /**
         * The precedence of the changes.
         */
        const changePrecedence = {
            'remove': 1,
            'add': 2,
            'changes': 3
        };
        /**
         * The changes to process after they have been filtered to remove
         * duplicate (and unnecessary) changes. Sort them according to
         * `changePrecedence`.
         */
        const sortedAndFilteredChanges = changes
            .filter((change, index) => {
            const isChanges = change.change === 'changes';
            const duplicates = [];
            const duplicateAddRemove = [];
            const duplicateChange = [];
            changes.forEach((searchChange, searchChangeIndex) => {
                if (searchChange.object.element === change.object.element) {
                    const item = { change: searchChange, index: searchChangeIndex };
                    duplicates.push(item);
                    if (searchChange.change !== 'changes') {
                        duplicateAddRemove.push(item);
                    }
                    else if (isChanges) {
                        duplicateChange.push(item);
                    }
                }
            });
            if (duplicates.length > 1) {
                const addRemoveStart = duplicateAddRemove.length
                    ? duplicateAddRemove[0].change.change
                    : '';
                const addRemoveEnd = duplicateAddRemove.length
                    ? duplicateAddRemove[duplicateAddRemove.length - 1].change.change
                    : '';
                if (isChanges) {
                    if (addRemoveStart !== '' && addRemoveStart === addRemoveEnd) {
                        return false;
                    }
                    else if (duplicateChange[duplicateChange.length - 1].index !== index) {
                        return false;
                    }
                }
                else {
                    if (addRemoveStart === '' || addRemoveStart !== addRemoveEnd) {
                        return false;
                    }
                    else if (duplicates[duplicates.length - 1].index !== index) {
                        return false;
                    }
                }
            }
            return true;
        })
            .sort((a, b) => changePrecedence[a.change] - changePrecedence[b.change]);
        /**
         * Go through the changes in `sortedAndFilteredChanges` and add operations
         * and callbacks for every change.
         */
        sortedAndFilteredChanges.forEach(change => {
            const actualChange = change.change;
            switch (actualChange) {
                case 'add':
                    if (change.type === 'list') {
                        const list = change.object;
                        this.interactableLists.push(list);
                        list.triggerDefined.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.position.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.orientation.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        list.focusLastFromParent.pipe(skip(1)).subscribe(() => {
                            this.enqueueChange({ object: list, type: 'list', change: 'changes' });
                        });
                        addOperation('listSetChildItems', list);
                        addOperation('listSetNestedLists', list, () => list.nested.forEach(nestedList => addOperation('listSetTriggerItem', nestedList)));
                        addOperation('listSetIntegratedItems', list, () => {
                            if (list.isSubList) {
                                const parent = list.parent ?? this.getClosestParent(list.element);
                                if (parent) {
                                    addOperation('listSetNestedLists', parent);
                                    addOperation('listSetTriggerItem', list);
                                    addOperation('listSetIntegratedItems', parent);
                                }
                            }
                            else {
                                const parentDisconnected = list.parentDisconnected ?? this.getClosestParent(list.element);
                                if (parentDisconnected) {
                                    addOperation('listSetNestedLists', parentDisconnected);
                                    addOperation('listSetIntegratedItems', parentDisconnected);
                                }
                            }
                        });
                    }
                    else {
                        const item = change.object;
                        this.interactableListItems.push(item);
                        addOperationCallback('listSetChildItems', () => {
                            const parent = item.parent ?? this.getClosestParent(item.element);
                            if (parent) {
                                addOperation('listSetChildItems', parent);
                                addOperationCallback('listSetNestedLists', () => {
                                    parent.nested.forEach(parentNestedList => addOperation('listSetTriggerItem', parentNestedList));
                                    const grandparent = parent.parent;
                                    if (grandparent) {
                                        addOperation('listSetIntegratedItems', grandparent);
                                    }
                                });
                                addOperation('listSetIntegratedItems', parent);
                            }
                        });
                    }
                    break;
                case 'remove':
                    if (change.type === 'list') {
                        const listIndex = this.interactableLists.findIndex(interactableList => interactableList.element === change.object.element);
                        const list = change.object;
                        this.interactableLists.splice(listIndex, 1);
                        list.nested.forEach(nestedList => addOperation('listSetTriggerItem', nestedList));
                        list.items.forEach(item => (item.parent = null));
                        list.integratedNestedItems.forEach(item => (item.parentIntegrationIndex = -1));
                        const integratedParentItem = list.integratedParentItem;
                        if (integratedParentItem) {
                            integratedParentItem.nestedIntegration = false;
                        }
                        const parent = list.parent;
                        if (parent) {
                            addOperation('listSetChildItems', parent);
                            addOperation('listSetNestedLists', parent);
                            addOperation('listSetIntegratedItems', parent);
                            const grandparent = parent.parent;
                            if (grandparent) {
                                addOperation('listSetIntegratedItems', grandparent);
                            }
                            list.parent = null;
                        }
                        else {
                            const parentDisconnected = list.parent;
                            if (parentDisconnected) {
                                addOperation('listSetChildItems', parentDisconnected);
                                addOperation('listSetNestedLists', parentDisconnected);
                                addOperation('listSetIntegratedItems', parentDisconnected);
                                list.parentDisconnected = null;
                            }
                            list.triggerDefined.complete();
                            list.position.complete();
                            list.orientation.complete();
                            list.focusLastFromParent.complete();
                        }
                        const parentItem = list.trigger;
                        if (parentItem) {
                            const listTrigger = list.trigger;
                            if (listTrigger && listTrigger.child?.element === list.element) {
                                listTrigger.child = null;
                            }
                        }
                    }
                    else {
                        const itemIndex = this.interactableListItems.findIndex(interactableListItem => interactableListItem.element === change.object.element);
                        const item = change.object;
                        this.interactableListItems.splice(itemIndex, 1);
                        const parent = item.parent;
                        if (parent) {
                            addOperation('listSetChildItems', parent);
                            const grandparent = parent.parent;
                            if (grandparent) {
                                addOperation('listSetIntegratedItems', grandparent);
                            }
                            const childList = item.child;
                            if (childList) {
                                item.child = null;
                                addOperation('listSetTriggerItem', childList);
                                addOperation('listSetIntegratedItems', parent);
                            }
                        }
                    }
                    break;
                case 'changes':
                    const changeList = change.object;
                    addOperation('listSetTriggerItem', changeList, () => {
                        const changeParent = changeList.parent;
                        if (changeParent) {
                            addOperation('listSetIntegratedItems', changeParent);
                        }
                    });
                    addOperation('listSetIntegratedItems', changeList);
                    break;
            }
        });
        /**
         * Call the queued operations and callbacks in order.
         * (Important) Callbacks can add operations, also for previously
         * executed operations.
         */
        const executeOperations = () => {
            for (let i = 0; i < operations.length; i++) {
                const currentOperation = operations[i];
                const operationObject = operationsToProcess[currentOperation];
                operationObject.lists.forEach(list => this[currentOperation](list));
                operationObject.lists = [];
                operationObject.callbacks.forEach(callback => callback());
                operationObject.callbacks = [];
                for (let u = 0; u < i + 1; u++) {
                    const currentCheckOperation = operations[u];
                    if (operationsToProcess[currentCheckOperation].lists.length > 0) {
                        executeOperations();
                        break;
                    }
                }
            }
        };
        executeOperations();
        /**
         * Call `listSetAllItemsQueryList` for every list that had
         * relevant changes.
         */
        this.interactableLists.forEach(interactableList => {
            if (interactableList.changeToProcess) {
                interactableList.changeToProcess = false;
                this.listSetAllItemsQueryList(interactableList);
            }
        });
        sortedAndFilteredChanges.forEach(change => {
            if (change.change === 'add' &&
                change.type === 'list' &&
                change.object.calculatedCallback) {
                change.object.calculatedCallback();
            }
        });
    }
    /**
     * Set all the child items of an interactable list.
     */
    listSetChildItems(list) {
        list.items.forEach(item => {
            if (item.parent?.element === list?.element) {
                item.parent = null;
            }
        });
        list.changeToProcess = true;
        list.items = this.getChildItems(list.element);
        list.items.forEach(item => {
            const oldParent = item.parent;
            if (oldParent && oldParent.element !== list.element) {
                const oldParentChildIndex = oldParent.items.findIndex(parentChildItem => parentChildItem.element === item.element);
                if (oldParentChildIndex >= 0) {
                    oldParent.changeToProcess = true;
                    oldParent.items.splice(oldParentChildIndex, 1);
                }
            }
            item.parent = list;
        });
    }
    /**
     * Set all the nested lists of an interactable list.
     */
    listSetNestedLists(list) {
        list.nested.forEach(nestedList => {
            if (nestedList.parent?.element === list?.element) {
                nestedList.parent = null;
            }
        });
        list.nestedDisconnected.forEach(nestedList => {
            if (nestedList.parentDisconnected?.element === list?.element) {
                nestedList.parentDisconnected = null;
            }
        });
        const subList = [];
        const nonSubList = [];
        for (const item of this.getNestedLists(list.element)) {
            if (item.isSubList) {
                subList.push(item);
            }
            else {
                nonSubList.push(item);
            }
        }
        list.nested = subList;
        subList.forEach(nestedList => {
            const oldParent = nestedList.parent;
            if (oldParent) {
                const oldParentNestedListIndex = oldParent.nested.findIndex(parentNestedList => parentNestedList.element === nestedList.element);
                if (oldParentNestedListIndex >= 0) {
                    oldParent.nested.splice(oldParentNestedListIndex, 1);
                }
            }
            nestedList.parent = list;
        });
        list.nestedDisconnected = nonSubList;
        nonSubList.forEach(nestedList => {
            const oldParent = nestedList.parentDisconnected;
            if (oldParent) {
                const oldParentNestedListIndex = oldParent.nestedDisconnected.findIndex(parentNestedList => parentNestedList.element === nestedList.element);
                if (oldParentNestedListIndex >= 0) {
                    oldParent.nestedDisconnected.splice(oldParentNestedListIndex, 1);
                }
            }
            nestedList.parentDisconnected = list;
        });
    }
    /**
     * Set the trigger/parent item of an interactable list.
     */
    listSetTriggerItem(list) {
        const listTrigger = list.trigger;
        if (listTrigger && listTrigger.child?.element === list.element) {
            listTrigger.child = null;
        }
        list.trigger = null;
        const setParentItem = list.triggerDefined.value;
        let parentItem;
        if (setParentItem) {
            const setParentItemHost = setParentItem.getHostElement();
            parentItem = this.interactableListItems.find(item => item.element === setParentItemHost);
        }
        else {
            parentItem = this.getClosestRelativeItem(list.element);
        }
        if (parentItem) {
            const oldChild = parentItem.child;
            if (oldChild) {
                oldChild.trigger = null;
            }
            parentItem.child = list;
            list.trigger = parentItem;
        }
        else {
            list.trigger = null;
        }
    }
    /**
     * Set the integrated items (nested or parent) of an interactable list.
     * This is used to connect sub/parent interactable lists depending on
     * the position of the nested interactable lists.
     */
    listSetIntegratedItems(list) {
        list.integratedNestedItems.forEach(integratedNestedItem => {
            integratedNestedItem.parentIntegrationIndex = -1;
        });
        list.integratedNestedItems = [];
        list.changeToProcess = true;
        list.nested.forEach(nestedList => {
            const integratedParentItem = nestedList.integratedParentItem;
            if (integratedParentItem) {
                integratedParentItem.nestedIntegration = false;
            }
            nestedList.integratedParentItem = null;
            nestedList.changeToProcess = true;
            const nestedListTrigger = nestedList.trigger;
            if (nestedListTrigger && nestedList.items.length > 0) {
                if (this.areParentIntegratedItemsAppropriate(nestedList)) {
                    if (nestedList.position.value === 'integrated') {
                        let actualReverse = nestedList.orientation.value === 'ets';
                        if (nestedList.focusLastFromParent.value) {
                            actualReverse = !actualReverse;
                        }
                        const integratedNestedItem = (actualReverse ? nestedList.items.slice().reverse() : nestedList.items).find(nestedListItem => !nestedListItem.child);
                        if (integratedNestedItem) {
                            const parentIntegrationIndex = list.items.findIndex(item => item.element === nestedListTrigger.element);
                            if (parentIntegrationIndex >= 0) {
                                integratedNestedItem.parentIntegrationIndex = parentIntegrationIndex;
                                list.integratedNestedItems.push(integratedNestedItem);
                            }
                        }
                    }
                    else {
                        nestedList.integratedParentItem = nestedListTrigger;
                        nestedListTrigger.nestedIntegration = true;
                    }
                }
            }
        });
    }
    /**
     * Set the `allItemsQueryList` of an interactable list.
     */
    listSetAllItemsQueryList(list) {
        const allItems = list.items.slice();
        let indexAddition = 0;
        const iter = (integratedItem) => {
            allItems.splice((integratedItem.parentIntegrationIndex === -1
                ? list.orientation.value === 'ets'
                    ? allItems.length
                    : -1
                : integratedItem.parentIntegrationIndex) +
                indexAddition +
                1, 0, integratedItem);
            indexAddition++;
        };
        if (list.orientation.value !== 'ets' && list.integratedParentItem) {
            iter(list.integratedParentItem);
        }
        list.integratedNestedItems.forEach(iter);
        if (list.orientation.value === 'ets' && list.integratedParentItem) {
            iter(list.integratedParentItem);
        }
        list.allItemsQueryList.reset(allItems.map(item => item.directive));
    }
    /**
     * Check if integrated items are appropriate in the parent of an interactable list.
     */
    areParentIntegratedItemsAppropriate(list) {
        const parent = list.parent;
        if (parent) {
            const orientation = list.orientation.value;
            const parentOrientation = parent.orientation.value;
            return ((orientation === 'vert' && parentOrientation !== 'vert') ||
                (orientation !== 'vert' && parentOrientation === 'vert'));
        }
        return false;
    }
    /**
     * Get the child items of an interactable list.
     */
    getChildItems(element, ret = []) {
        if (this.interactableListItems.length) {
            const iter = (child) => {
                const childResult = this.interactableListItems.find(item => item.element === child);
                if (childResult) {
                    ret.push(childResult);
                }
                else if (!this.interactableLists.find(list => list.element === child)) {
                    this.getChildItems(child, ret);
                }
            };
            Array.from(element.children).forEach(iter);
            Array.from(element.dropdownChildren || []).forEach(iter);
        }
        return ret;
    }
    /**
     * Get the nested lists of an interactable list.
     */
    getNestedLists(element, ret = []) {
        if (this.interactableLists.length) {
            const iter = (child) => {
                const childResult = this.interactableLists.find(list => list.element === child);
                if (childResult) {
                    ret.push(childResult);
                }
                else {
                    this.getNestedLists(child, ret);
                }
            };
            Array.from(element.children).forEach(iter);
            Array.from(element.dropdownChildren || []).forEach(iter);
        }
        return ret;
    }
    /**
     * Get the closest relative/parent/trigger item of an interactable list.
     * @param onlyCheckParent Determines if the children of parents should also be checked or ignored.
     */
    getClosestRelativeItem(element, onlyCheckParent = false) {
        if (this.interactableListItems.length) {
            let previousElement;
            let currentElement = element;
            let outOfMenu = false;
            while (currentElement.parentElement && !outOfMenu) {
                previousElement = currentElement;
                const checkElement = (currentElement =
                    currentElement.dropdownParentElement ?? currentElement.parentElement);
                const item = this.interactableListItems.find(interactableListItem => interactableListItem.element === checkElement && interactableListItem.parent);
                if (item) {
                    return item;
                }
                if (!onlyCheckParent) {
                    const childResult = this.getClosestChildItemIndex(currentElement, previousElement);
                    if (childResult !== null && childResult >= 0) {
                        return this.interactableListItems[childResult];
                    }
                    if (currentElement !== element &&
                        this.interactableLists.find(interactableList => interactableList.element === checkElement)) {
                        outOfMenu = true;
                    }
                }
            }
        }
        return null;
    }
    /**
     * Get the closest parent list of an interactable list.
     */
    getClosestParent(element) {
        if (this.interactableLists.length > 0) {
            let currentElement = element;
            while (currentElement.parentElement) {
                const checkElement = (currentElement =
                    currentElement.dropdownParentElement ?? currentElement.parentElement);
                const parent = this.interactableLists.find(interactableList => interactableList.element === checkElement);
                if (parent) {
                    return parent;
                }
            }
        }
        return null;
    }
    /**
     * Get the index of the closest child item.
     */
    getClosestChildItemIndex(element, currentChild, reverse = false) {
        if (this.interactableListItems.length < 0) {
            return null;
        }
        if (!element.children.length && !element.dropdownChildren?.length) {
            return null;
        }
        const children = [
            ...Array.from(element.children),
            ...Array.from(element.dropdownChildren || [])
        ];
        if (currentChild) {
            const currentElementIndex = children.indexOf(currentChild);
            let currentChildAppeared = false;
            const result = [];
            children.forEach((child, index) => {
                if (child !== currentChild) {
                    const childIndex = this.interactableListItems.findIndex(item => item.element === child);
                    if (childIndex === -1) {
                        const subResult = this.getClosestChildItemIndex(child, undefined, !currentChildAppeared);
                        if (subResult !== null) {
                            result.push({ itemIndex: subResult, elementIndex: index });
                        }
                    }
                    else {
                        result.push({ itemIndex: childIndex, elementIndex: index });
                    }
                }
                else {
                    currentChildAppeared = true;
                }
            });
            result.sort((firstItem, secondItem) => Math.abs(currentElementIndex - firstItem.elementIndex) -
                Math.abs(currentElementIndex - secondItem.elementIndex));
            return result.length ? result[0].itemIndex : null;
        }
        else {
            const correctedChildren = reverse ? children.reverse() : children;
            for (const child of correctedChildren) {
                const childIndex = this.interactableListItems.findIndex(item => item.element === child);
                if (childIndex === -1) {
                    const subResult = this.getClosestChildItemIndex(child, undefined, reverse);
                    if (subResult !== null) {
                        return subResult;
                    }
                }
                else {
                    return childIndex;
                }
            }
        }
        return null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiListInteractionItemDirective {
    /**
     * Whether the list item interaction is disabled.
     */
    listInteractionItemDisabled = false;
    get disabled() {
        return (!(this.element.nativeElement.offsetParent ||
            (this.isBrowser &&
                window.getComputedStyle(this.element.nativeElement).position === 'fixed' &&
                window.getComputedStyle(this.element.nativeElement).display !== 'none')) || this.listInteractionItemDisabled);
    }
    /**
     * An optional siDropdownToggle instance to automatically open and close the dropdown on list interaction navigation
     */
    listInteractionItemDropdownToggle;
    get dropdownToggle() {
        return this.listInteractionItemDropdownToggle;
    }
    interactableListItem;
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    element = inject(ElementRef);
    listInteractionService = inject(SiListInteractionService);
    get parentList() {
        if (this.interactableListItem?.parent) {
            return this.interactableListItem.parent.directive;
        }
        return null;
    }
    get childList() {
        if (this.interactableListItem?.child &&
            this.interactableListItem.child.trigger?.element === this.interactableListItem.element) {
            return this.interactableListItem.child.directive;
        }
        return null;
    }
    ngOnInit() {
        this.interactableListItem = this.listInteractionService.addInteractableListItem(this, this.element.nativeElement);
    }
    ngOnDestroy() {
        if (this.interactableListItem) {
            this.listInteractionService.removeInteractableListItem(this.interactableListItem);
        }
    }
    /** Focuses the list item. */
    focus(origin, options) {
        if (this.interactableListItem.parent) {
            this.interactableListItem.parent.directive.setActiveItem(this);
        }
        this.element.nativeElement.focus(options);
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this.element.nativeElement;
    }
    hasChildElement(element) {
        return this.interactableListItem
            ? this.listInteractionService.isChildElementOfItem(this.interactableListItem, element)
            : this.element.nativeElement.contains(element);
    }
    getItemElement() {
        return this.getHostElement();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiListInteractionItemDirective, selector: "[siListInteractionItem]", inputs: { listInteractionItemDisabled: "listInteractionItemDisabled", listInteractionItemDropdownToggle: "listInteractionItemDropdownToggle" }, host: { attributes: { "tabindex": "-1" } }, exportAs: ["si-list-interaction-item"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siListInteractionItem]',
                    exportAs: 'si-list-interaction-item',
                    host: {
                        tabindex: '-1'
                    }
                }]
        }], propDecorators: { listInteractionItemDisabled: [{
                type: Input
            }], listInteractionItemDropdownToggle: [{
                type: Input
            }] } });

class SiListInteractionDirective {
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    _elementRef = inject((ElementRef));
    _changeDetector = inject(ChangeDetectorRef);
    listInteractionService = inject(SiListInteractionService);
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link listInteractionSubListStart}
     * @deprecated Use the new Input {@link listInteractionSubListStart}
     */
    set listInteractionSubListLeft(value) {
        this.listInteractionSubListStart = value;
    }
    get listInteractionSubListLeft() {
        return this.listInteractionSubListStart;
    }
    /**
     * Specifies whether sub lists are displayed on the start side.
     */
    listInteractionSubListStart = false;
    get start() {
        return this.orientation === 'vert' && this.listInteractionSubListStart;
    }
    /**
     * Specifies whether the component should automatically be focused as soon as it is loaded.
     */
    listInteractionFocusOnLoad = false;
    /**
     * Specifies if any and which item should be focused when the component is focused,
     */
    listInteractionFocus = '';
    _focusLastFromParent = false;
    /**
     * Specifies if the last item should be focused when navigating to this list from a parent list (instead of the first).
     * @defaultref {@link _focusLastFromParent}
     */
    set listInteractionFocusLastFromParent(focusLastFromParent) {
        this._focusLastFromParent = focusLastFromParent;
        if (this.interactableList) {
            this.interactableList.focusLastFromParent.next(focusLastFromParent);
        }
    }
    get listInteractionFocusLastFromParent() {
        return this._focusLastFromParent;
    }
    get autofocusFromParent() {
        return this.listInteractionAutofocusFromParent;
    }
    listInteractionAutofocusFromParent = false;
    /**
     * Specifies whether the list is a sub list and a parent item should automatically be searched or be defined.
     */
    listInteractionIsSubList = false;
    _parentItem = null;
    /**
     * Specifies a specific item that is the parent of the menu if the automatic one is incorrect.
     * @defaultref {@link _parentItem}
     */
    set listInteractionParentItem(item) {
        this._parentItem = item;
        if (this.interactableList) {
            this.interactableList.triggerDefined.next(item);
        }
    }
    get listInteractionParentItem() {
        if (!this._parentItem && this.interactableList?.trigger) {
            return this.interactableList.trigger.directive;
        }
        return this._parentItem;
    }
    get parentItem() {
        return this.listInteractionParentItem;
    }
    _position = 'integrated';
    /**
     * Specifies the position of the list relative to the parent.
     * Only relevant if this is a sub list and the parent has a different orientation.
     * @defaultref {@link _position}
     */
    set listInteractionPosition(position) {
        this._position = position;
        if (this.interactableList) {
            this.interactableList.position.next(position);
        }
    }
    get listInteractionPosition() {
        return this._position;
    }
    _orientation = 'ste';
    /**
     * Specifies the orientation of the list, which signifies how it should be navigated.
     * The default is horizontal start to end.
     * @defaultref {@link _orientation}
     */
    set listInteractionOrientation(orientation) {
        const deprecatedMessage = '@simpl/element-ng - SiListInteractionDirective: \
Setting listInteractionOrientation to "ltr" or "rtl" is deprecated, use "ste" or "ets" instead';
        if (orientation === 'ltr') {
            console.warn(deprecatedMessage);
            this._orientation = 'ste';
        }
        else if (orientation === 'rtl') {
            console.warn(deprecatedMessage);
            this._orientation = 'ets';
        }
        else {
            this._orientation = orientation;
        }
        if (this.interactableList) {
            this.interactableList.orientation.next(this._orientation);
        }
    }
    get listInteractionOrientation() {
        return this._orientation;
    }
    get orientation() {
        return this._orientation;
    }
    /**
     * Automatically refocus after pressing enter on one of the items.
     * Use when losing focus causes issues with keyboard interaction.
     */
    listInteractionRefocusAfterEnter = false;
    /**
     * Whether a hidden `dropdown-menu` or `si-menu` sub-menu
     * should be shown when the parent item is focused.
     */
    listInteractionShowSubListOnParentFocus = true;
    /**
     * Specifies whether the currently open item should be closed on click outside.
     */
    listInteractionCloseOnClickOut = true;
    hadShowClassBefore = false;
    keepOpen = false;
    setKeepOpen(state) {
        if (state && !this.keepOpen) {
            this.keepOpen = true;
            this.hadShowClassBefore = this._elementRef.nativeElement.classList.contains('show');
            this._elementRef.nativeElement.classList.add('show');
        }
        else if (!state && this.keepOpen) {
            this.keepOpen = false;
            if (!this.hadShowClassBefore) {
                this._elementRef.nativeElement.classList.remove('show');
            }
            this.hadShowClassBefore = false;
        }
    }
    tabindex = '';
    interactableList;
    get parentList() {
        if (this.interactableList?.parent) {
            return this.interactableList.parent.directive;
        }
        return null;
    }
    get parentListAll() {
        if (this.interactableList &&
            (this.interactableList.parent || this.interactableList.parentDisconnected)) {
            return this.interactableList.parent
                ? this.interactableList.parent.directive
                : this.interactableList.parentDisconnected.directive;
        }
        return null;
    }
    connectionsCalculated = false;
    _keyManager;
    _previousChild;
    _ignoreKeydown = undefined;
    _orientationChangeSubscription;
    _changeSubscription;
    _tabOutSubscription;
    ignoreNextChange = false;
    ngAfterViewInit() {
        this._keyManager = new FocusKeyManager(this.interactableList.allItemsQueryList)
            .withWrap()
            .withAllowedModifierKeys(['shiftKey']);
        this._orientationChangeSubscription = this.interactableList.orientation.subscribe(orientation => {
            this._orientation = orientation;
            if (orientation === 'vert') {
                this._keyManager.withHorizontalOrientation(null).withVerticalOrientation();
            }
            else {
                this._keyManager
                    .withVerticalOrientation(false)
                    .withHorizontalOrientation(orientation === 'ste' ? 'ltr' : 'rtl');
            }
        });
        this._changeSubscription = this._keyManager.change.subscribe(() => {
            if (this.listInteractionShowSubListOnParentFocus && !this.ignoreNextChange) {
                this.openCloseAndFocusChildren();
                addEventListener('click', this.onClickOut);
            }
            this.ignoreNextChange = false;
        });
        this._tabOutSubscription = this._keyManager.tabOut.subscribe(() => {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        });
    }
    ngOnInit() {
        this.interactableList = this.listInteractionService.addInteractableList(this, this._elementRef.nativeElement, this._parentItem, this._position, this._orientation, this._focusLastFromParent, this.listInteractionIsSubList, () => this.calculated());
        const newTabindex = !this.listInteractionIsSubList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
    }
    ngOnDestroy() {
        this._orientationChangeSubscription?.unsubscribe();
        this._changeSubscription?.unsubscribe();
        this._tabOutSubscription?.unsubscribe();
        if (this.interactableList) {
            this.listInteractionService.removeInteractableList(this.interactableList);
        }
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
        }
    }
    calculated() {
        this.connectionsCalculated = true;
        const newTabindex = !this.listInteractionIsSubList || !this.parentList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
        if (this.isBrowser && document.activeElement === this.getHostElement()) {
            if (this.listInteractionFocus) {
                this.focusFirstItem('program', this.listInteractionFocus === 'last');
            }
        }
        else if (this.listInteractionFocusOnLoad) {
            this._elementRef.nativeElement.focus();
        }
    }
    /** Ignore the next keydown in this and all parent lists. Called from sub list. */
    ignoreKeydown(event) {
        if (this.parentListAll) {
            this.parentListAll.ignoreKeydown(event);
        }
        this._ignoreKeydown = event;
    }
    /** Handle a keyboard event from the list, delegating to the appropriate action. */
    handleKeydown(event) {
        if (this._ignoreKeydown?.key === event.key && this._ignoreKeydown.target === event.target) {
            this._ignoreKeydown = undefined;
            return;
        }
        if (this.parentListAll) {
            // If this is a sub list ignore the keydown in all parent lists (due to event bubbling).
            this.parentListAll.ignoreKeydown(event);
        }
        const manager = this._keyManager;
        const rtlCorrectedKey = correctKeyRTL(event.key);
        switch (rtlCorrectedKey) {
            case 'Escape':
                if (!event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                    event.preventDefault();
                    this.goDownLevel();
                }
                break;
            case 'ArrowUp':
                if (this._orientation !== 'vert') {
                    this.goDownLevel();
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowDown':
                if (this.orientation !== 'vert') {
                    this.goUpLevel();
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowLeft':
                if (this._orientation === 'vert') {
                    if (this.listInteractionSubListStart) {
                        this.goUpLevel(false, true);
                    }
                    else {
                        this.goDownLevel();
                    }
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'ArrowRight':
                if (this._orientation === 'vert') {
                    if (this.listInteractionSubListStart) {
                        this.goDownLevel(false, true);
                    }
                    else {
                        this.goUpLevel();
                    }
                }
                else {
                    manager.onKeydown(event);
                }
                break;
            case 'Enter':
                if (this.listInteractionRefocusAfterEnter) {
                    setTimeout(() => {
                        if (this.isBrowser &&
                            (!document.activeElement || document.activeElement === document.body)) {
                            this.focusActiveItem();
                            this.openCloseAndFocusChildren(true);
                        }
                    });
                }
                break;
            default:
                manager.onKeydown(event);
        }
    }
    onClickOut = (event) => this.clickOutHandler(event);
    clickOutHandler(event) {
        const target = event.target;
        if (this.listInteractionCloseOnClickOut &&
            this.interactableList &&
            !this._keyManager?.activeItem?.hasChildElement(target)) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        }
    }
    closePreviousChild() {
        if (this._previousChild) {
            this._previousChild.close();
            this._previousChild = undefined;
            this._changeDetector.detectChanges();
        }
    }
    openCloseAndFocusChildren(skipOpenClose = false) {
        if (!skipOpenClose) {
            this.closePreviousChild();
        }
        const active = this._keyManager.activeItem;
        if (active) {
            if (active.dropdownToggle) {
                active.dropdownToggle.open();
                this._previousChild = { close: () => active.dropdownToggle?.close() };
                this._changeDetector.detectChanges();
            }
            else {
                const activeList = this.getItemChildren(active);
                if (activeList && activeList.getHostElement() !== this.getHostElement()) {
                    if (!skipOpenClose) {
                        activeList.open();
                        this._previousChild = activeList;
                        this._changeDetector.detectChanges();
                    }
                    if (activeList.autofocusFromParent) {
                        Promise.resolve().then(() => activeList.focusFirstItem());
                    }
                }
            }
        }
    }
    open() {
        this.setKeepOpen(true);
    }
    close() {
        this.setKeepOpen(false);
    }
    setActiveItem(activeItem, ignoreNextChange = false) {
        if (!this._keyManager.activeItem ||
            activeItem.getHostElement() !== this._keyManager.activeItem.getHostElement()) {
            this.ignoreNextChange = ignoreNextChange;
            this._keyManager.setActiveItem(activeItem);
        }
    }
    focusActiveItem(origin = 'program', reverse = false, nestedItem) {
        let active = this._keyManager.activeItem;
        if (!active && nestedItem && this.interactableList) {
            const foundActiveItem = this.interactableList.allItemsQueryList.find(listItem => nestedItem.getHostElement() === listItem.getHostElement());
            if (foundActiveItem) {
                this._keyManager.setActiveItem(foundActiveItem);
                active = this._keyManager.activeItem;
            }
        }
        if (active) {
            if (active && nestedItem && nestedItem.getHostElement() === active?.getHostElement()) {
                if (reverse) {
                    this._keyManager.setNextItemActive();
                }
                else {
                    this._keyManager.setPreviousItemActive();
                }
            }
            const activeIndex = this._keyManager.activeItemIndex;
            const newActive = this._keyManager.activeItem;
            if (newActive) {
                if (activeIndex &&
                    this.interactableList?.allItemsQueryList.get(activeIndex)?.getHostElement() !==
                        newActive?.getHostElement()) {
                    this._keyManager.setActiveItem(newActive);
                }
                else {
                    newActive.focus();
                }
            }
        }
    }
    /**
     * Focus the first item in the list.
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusFirstItem(origin = 'program', reverse = false, fromParent = false) {
        const itemIndexes = Array.from(this.interactableList.allItemsQueryList)
            .map((item, index) => {
            if (item.parentList &&
                !item.disabled &&
                item.parentList.getHostElement() === this.getHostElement()) {
                return index;
            }
            else {
                return -1;
            }
        })
            .filter(index => index !== -1);
        if (itemIndexes.length > 0) {
            let actualReverse = reverse;
            if (this.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (fromParent && this.listInteractionFocusLastFromParent) {
                actualReverse = !actualReverse;
            }
            const actualIndex = actualReverse ? itemIndexes[itemIndexes.length - 1] : itemIndexes[0];
            const activeItem = this.interactableList.allItemsQueryList.get(actualIndex);
            const activeItemHost = activeItem.getHostElement();
            if (this.interactableList.parent?.integratedNestedItems.find(item => item.element === activeItemHost)) {
                this.parentList.setActiveItem(activeItem);
            }
            else if (this.interactableList.integratedParentItem) {
                this.parentList.setActiveItem(this.interactableList.integratedParentItem.directive);
            }
            if (this._keyManager.activeItem?.getHostElement() === activeItem.getHostElement()) {
                this.focusActiveItem();
                this.openCloseAndFocusChildren();
                addEventListener('click', this.onClickOut);
            }
            else {
                this._keyManager.setFocusOrigin(origin).setActiveItem(activeItem);
            }
        }
    }
    getItemChildren(item) {
        return item.childList;
    }
    goDownLevel(reverse = false, start = false) {
        if (this.parentList) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
            const nestedOrParentItem = this.interactableList
                ? (this.interactableList.integratedParentItem
                    ? this.interactableList.integratedParentItem
                    : this.interactableList.items.find(item => item.parentIntegrationIndex !== -1 || item.nestedIntegration))?.directive
                : undefined;
            let actualReverse = reverse;
            const parent = this.parentList;
            if (parent.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (start && !parent.start) {
                actualReverse = !actualReverse;
            }
            parent.focusActiveItem('program', actualReverse, nestedOrParentItem);
        }
        else {
            this.goUpLevel(true);
        }
    }
    goUpLevel(reverse = false, start = false) {
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = this.getItemChildren(active);
            if (activeList) {
                activeList.focusFirstItem('program', reverse, true);
            }
            else if (this.parentList) {
                this.goDownLevel(true, start);
            }
        }
    }
    focusInEvent(target) {
        if (this.connectionsCalculated) {
            if (target === this.getHostElement()) {
                if (this.listInteractionFocus) {
                    this.focusFirstItem('program', this.listInteractionFocus === 'last');
                }
            }
            else {
                if (this.interactableList) {
                    const foundItem = this.interactableList.items.find(item => item.element.contains(target));
                    if (foundItem) {
                        this.setActiveItem(foundItem.directive, true);
                    }
                }
                Promise.resolve().then(() => {
                    if (this.tabindex !== '-1') {
                        this.tabindex = '-1';
                    }
                });
            }
        }
    }
    focusOutEvent() {
        if (this.tabindex === '-1') {
            Promise.resolve().then(() => (this.tabindex = !this.listInteractionIsSubList || !this.parentList ? '0' : ''));
        }
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiListInteractionDirective, selector: "[siListInteraction]", inputs: { listInteractionSubListLeft: "listInteractionSubListLeft", listInteractionSubListStart: "listInteractionSubListStart", listInteractionFocusOnLoad: "listInteractionFocusOnLoad", listInteractionFocus: "listInteractionFocus", listInteractionFocusLastFromParent: "listInteractionFocusLastFromParent", listInteractionAutofocusFromParent: "listInteractionAutofocusFromParent", listInteractionIsSubList: "listInteractionIsSubList", listInteractionParentItem: "listInteractionParentItem", listInteractionPosition: "listInteractionPosition", listInteractionOrientation: "listInteractionOrientation", listInteractionRefocusAfterEnter: "listInteractionRefocusAfterEnter", listInteractionShowSubListOnParentFocus: "listInteractionShowSubListOnParentFocus", listInteractionCloseOnClickOut: "listInteractionCloseOnClickOut" }, host: { listeners: { "keydown": "handleKeydown($event)", "focusin": "focusInEvent($event.target)", "focusout": "focusOutEvent()" }, properties: { "attr.tabindex": "this.tabindex" } }, exportAs: ["si-list-interaction"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siListInteraction]',
                    exportAs: 'si-list-interaction'
                }]
        }], propDecorators: { listInteractionSubListLeft: [{
                type: Input
            }], listInteractionSubListStart: [{
                type: Input
            }], listInteractionFocusOnLoad: [{
                type: Input
            }], listInteractionFocus: [{
                type: Input
            }], listInteractionFocusLastFromParent: [{
                type: Input
            }], listInteractionAutofocusFromParent: [{
                type: Input
            }], listInteractionIsSubList: [{
                type: Input
            }], listInteractionParentItem: [{
                type: Input
            }], listInteractionPosition: [{
                type: Input
            }], listInteractionOrientation: [{
                type: Input
            }], listInteractionRefocusAfterEnter: [{
                type: Input
            }], listInteractionShowSubListOnParentFocus: [{
                type: Input
            }], listInteractionCloseOnClickOut: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], focusInEvent: [{
                type: HostListener,
                args: ['focusin', ['$event.target']]
            }], focusOutEvent: [{
                type: HostListener,
                args: ['focusout']
            }] } });

class SiListInteractionModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionModule, declarations: [SiListInteractionDirective, SiListInteractionItemDirective], imports: [CommonModule], exports: [SiListInteractionDirective, SiListInteractionItemDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiListInteractionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SiListInteractionDirective, SiListInteractionItemDirective],
                    exports: [SiListInteractionDirective, SiListInteractionItemDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiListInteractionDirective, SiListInteractionItemDirective, SiListInteractionModule, SiListInteractionService };
